// src/stores/contactStore.js

import { defineStore } from "pinia";
import service from "@/services/crm/contact.service";
import * as Toast from "@/utils/toast";
import { createScopedCache } from "./_cache";

// ✅ Helper to handle different toast API formats
const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

const contactListCache = createScopedCache("contacts:list");
const contactDetailCache = createScopedCache("contacts:detail");

const CONTACTS_LIST_TTL = 2 * 60 * 1000;
const CONTACTS_DETAIL_TTL = 5 * 60 * 1000;

export const useContactStore = defineStore("contactStore", {
  state: () => ({
    items: [],
    byId: Object.create(null),
    loading: false,
    pagination: { current_page: 1, per_page: 25, total: 0 },
    filters: { q: "", status: "", owner_id: "", date_from: "", date_to: "", with_deals:1 },
  }),
  actions: {
    normalize(raw = {}) {
      const name =
        raw.name ||
        [raw.first_name, raw.last_name].filter(Boolean).join(" ") ||
        "-";
      return {
        id: raw.id,
        first_name: raw.first_name || "",
        last_name: raw.last_name || "",
        name,
        job_title: raw.job_title || "",
        client_id: raw.client_id ?? raw.client?.id ?? null,
        company_name: raw.company_name || "",
        client: raw.client || null,
        email: raw.email || "",
        phone: raw.phone || "",
        phone_alt: raw.phone_alt || "",
        owner: raw.owner || null,
        owner_id: raw.owner_id ?? raw.owner?.id ?? null,
        status: raw.status || "",
        rating: raw.rating || null,
        source: raw.source || "",
        birthday: raw.birthday || null,
        address: raw.address || "",
        city: raw.city || "",
        state: raw.state || "",
        country: raw.country || "",
        zipcode: raw.zipcode || "",
        facebook: raw.facebook || "",
        twitter: raw.twitter || "",
        linkedin: raw.linkedin || "",
        skype: raw.skype || "",
        whatsapp: raw.whatsapp || "",
        instagram: raw.instagram || "",
        tags: Array.isArray(raw.tags) ? raw.tags : [],
        notes: raw.notes || "",
        created_at: raw.created_at || null,
        updated_at: raw.updated_at || null,
        // include deals if present (from show or index?with_deals=1)
        deals: Array.isArray(raw.deals) ? raw.deals : [],
        deal_ids: Array.isArray(raw.deals)
          ? raw.deals.map((d) => d.id)
          : Array.isArray(raw.deal_ids)
          ? raw.deal_ids
          : [],
      };
    },

    upsertOne(raw) {
      const n = this.normalize(raw);
      this.byId[n.id] = n;
      const idx = this.items.findIndex((x) => x.id === n.id);
      if (idx === -1) this.items.unshift(n);
      else this.items.splice(idx, 1, n);
      return n;
    },

    // ✅ Added 'list' alias to match smartFetch expectations
    async list(extra = {}) {
      return await this.fetchAll(extra);
    },

    async fetchAll(extra = {}) {
      this.loading = true;
      try {
        const params = { ...this.filters, ...extra };
        const payload = await contactListCache.get(params, {
          ttlMs: CONTACTS_LIST_TTL,
          force: extra.force === true,
          loader: async () => {
            const { data } = await service.getAll(params);
            return {
              items: data.data || [],
              meta: {
                current_page: data.meta?.current_page || 1,
                per_page: data.meta?.per_page || 25,
                total: data.meta?.total || (data.data || []).length,
              },
            };
          },
          onHydrate: ({ items, meta }) => {
            this.items = (items || []).map((r) => this.upsertOne(r));
            this.pagination = meta || this.pagination;
          },
        });

        // ✅ Return in format expected by smartFetch
        return {
          data: {
            data: this.items,
            meta: this.pagination,
          },
        };
      } catch (e) {
        notifyError(e?.response?.data?.message || "Failed to load contacts");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async ensureList(extra = {}) {
      return await this.fetchAll({ ...extra, force: false });
    },

    async fetchOne(id) {
      if (!id) return null;
      this.loading = true;
      try {
        const payload = await contactDetailCache.get(
          { id: Number(id) || id },
          {
            ttlMs: CONTACTS_DETAIL_TTL,
            loader: async () => {
              const { data } = await service.get(id);
              return { contact: data };
            },
            onHydrate: ({ contact }) => {
              if (contact) this.upsertOne(contact);
            },
          }
        );
        return payload.contact ? this.upsertOne(payload.contact) : null;
      } catch (e) {
        notifyError(e?.response?.data?.message || "Failed to load contact");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async get(id) {
      return await this.fetchOne(id);
    },

    async show(id) {
      return await this.fetchOne(id);
    },

    async find(id) {
      return await this.fetchOne(id);
    },

    async create(payload) {
      this.loading = true;
      try {
        await service.create(payload);
        this.invalidateCaches();
        notifySuccess("Contact created");
        await this.fetchAll({ page: 1, force: true });
      } catch (e) {
        notifyError(e?.response?.data?.message || "Failed to create contact");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async update(id, payload) {
      this.loading = true;
      try {
        await service.update(id, payload);
        this.invalidateCaches(id);
        notifySuccess("Contact updated");
        await this.fetchAll({ force: true }); // keep list in sync
      } catch (e) {
        notifyError(e?.response?.data?.message || "Failed to update contact");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async remove(id) {
      this.loading = true;
      try {
        await service.delete(id);
        this.invalidateCaches(id);
        notifySuccess("Contact deleted");
        await this.fetchAll({ page: 1, force: true });
      } catch (e) {
        notifyError(e?.response?.data?.message || "Failed to delete contact");
        throw e;
      } finally {
        this.loading = false;
      }
    },

    async ensureOne(id) {
      return await this.fetchOne(id);
    },

    invalidateCaches(id = null) {
      contactListCache.clear();
      if (id != null) {
        contactDetailCache.invalidate({ id: Number(id) || id });
      } else {
        contactDetailCache.clear();
      }
    },
  },
});
